import { Component, OnInit } from '@angular/core';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { DisplayOptions } from 'app/modules/shared/interfaces/display-options';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { Router } from '@angular/router';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, State, orderBy, filterBy } from '@progress/kendo-data-query';
import { IRankings, IRanksParameters } from '../../interfaces/performance-rankings';
import { RankingsService } from '../../services/rankings.service';
import {
    NoDataTypes,
    NoDataActionType,
    NoDataSourceType,
} from 'app/modules/shared/interfaces/no-data';
import { LimeGearCharts } from 'app/modules/shared/classes/chart-data';
import * as Highcharts from 'highcharts';
import {
    IPositionInfo,
    IPositionParameters,
    PositionService,
} from '../../../shared/services/position.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import {
    IPaginatedDataInfo,
    ITableColumnProperties,
    ITableDataProperties,
} from 'app/modules/shared/components/common-kendo-table/common-kendo-table.component';
import {
    IChartParameters,
    SparklineChartOptions,
} from 'app/modules/shared/classes/sparkline-chart-options';
import { ITableColumns } from 'app/modules/reports/interfaces/reporting-data';
import { IWeightedScoreData } from '../../interfaces/performance-entity-data';

@Component({
    selector: 'app-performance-rankings-position',
    templateUrl: './performance-rankings-position.component.html',
    styleUrls: ['./performance-rankings-position.component.scss'],
})
export class PerformanceRankingsPositionComponent implements OnInit {
    positionList: IPositionInfo[];
    selectedPosition: IPositionInfo[];
    positionListSettings: any;
    pageHeadline: IPageHeadline;
    hFilterJSON: string = null;
    displayOptions: DisplayOptions[];
    gridView: GridDataResult;
    skip = 0;
    pageSize = 10;
    sort: SortDescriptor[] = [];
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };
    gridState: State = {
        sort: [],
        skip: 0,
        take: 10,
    };
    scrollable = true;
    type: 'numeric' | 'input' = 'input';
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };
    rankEntrySort: SortDescriptor[] = [{ field: 'rank' }];
    rankingData: IRankings[] = [];
    loading: boolean = false;
    rankSearchTerm: string = '';
    noDataType: NoDataTypes;
    noDataForCompany: boolean;

    datePeriod: string;
    ymc: string;
    limeGearCharts: LimeGearCharts.LimeGearChart;
    changeToggle: boolean = false;
    exportFileName: string;
    exportData: IRankings[];
    tableDataProperties: ITableDataProperties;
    tableColumnProperties: ITableColumnProperties;
    action: NoDataActionType;
    hierarchyIdentifier: string;
    currentPeriodData: IWeightedScoreData[];
    fullDataset: IWeightedScoreData[];
    paginatedData: IWeightedScoreData[];
    spco: SparklineChartOptions;

    constructor(
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private rankingsService: RankingsService,
        private positionService: PositionService
    ) {}

    ngOnInit() {
        this.getExcelExportData = this.getExcelExportData.bind(this);
        this.hFilterJSON = this._reusableFunctions.getHierarchyFilters();
        this.positionListSettings = {
            singleSelection: true,
            text: 'Select Position',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'position',
            primaryKey: 'positionIdentifier',
            enableFilterSelectAll: false,
        };

        this.pageHeadline = {
            module: 'performance',
            moduleIcon: 'fa-heartbeat',
            pageTitle: 'Position Rankings by Weighted Score',
            linkArray: [
                {
                    moduleLink: 'performance/insights',
                    linkTitle: 'Performance Insights',
                    showLink: true,
                },
            ],
        };

        this.displayOptions = this._reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.noDataType = {
            action: NoDataActionType.emptySelect,
            searchTerm: '',
            componentTitle: 'Leaderboard by Weighted Score',
            inputSource: NoDataSourceType.table,
        };

        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this._router,
            this._reusableFunctions
        );
        this.currentPeriodData = [];
        this.spco = new SparklineChartOptions(this._reusableFunctions);
        this.initiateTableProperties();
        this.setDisplayOptionsSelected();
        this.getPositions();
    }

    getPositions() {
        this.positionList = [];
        this.selectedPosition = [];
        this.noDataForCompany = true;

        let parameters: IPositionParameters = {
            ymc: this.ymc,
            fromDatePeriod: this.datePeriod,
            getEmployeeAssignned: '1',
        };

        let positionID = history.state.role ? +history.state.role : -1;
        this.positionService.getScorecardPositions(parameters).subscribe(
            (res) => {
                if (res.length > 0) {
                    if (positionID > -1) {
                        let posInf = res.find((p) => p.positionIdentifier === positionID);
                        this.selectedPosition = posInf ? [posInf] : [res[0]];
                    } else if (this.selectedPosition?.length === 0) {
                        let posInf = res.find((pos) => pos.positionAbbv.toLowerCase() === 'lo');
                        this.selectedPosition = posInf ? [posInf] : [res[0]];
                    }
                }
                this.positionList = res;
                this.getData();
            },
            () => {}
        );
    }

    applyNewFilters(event: string) {
        this.skip = 0;
        this.hFilterJSON = event;
        this.setNodataType(NoDataActionType.filter);
        this.getData();
    }

    setDisplayOptionsSelected() {
        this.datePeriod = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === 'daterangerolling'
        )?.selectedOptions;
        this.ymc = this.displayOptions.find(
            (item) => item.optionKey.toLowerCase() === 'scorecardasof'
        )?.selectedOptions;
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.skip = 0;
        this.displayOptions = event;
        this.setNodataType(NoDataActionType.filter);
        this.setDisplayOptionsSelected();
        this.getData();
    }

    resetChartData() {
        this.tableDataProperties.gridData = [];
        this.tableDataProperties.hasData = false;
        this.tableDataProperties.isDataLoaded = false;
        this.tableDataProperties = Object.assign({}, this.tableDataProperties);
    }

    getData() {
        this.loading = true;
        let parameters: IRanksParameters = {
            datePeriod: this.datePeriod ?? '',
            entityType: '',
            entityCode: this.selectedPosition
                ? this.selectedPosition[0]?.positionIdentifier.toString()
                : '',
            hFilterJSON: this.hFilterJSON,
            ymc: this.ymc ?? '',
        };
        this.resetChartData();

        if (this.ymc != undefined) {
            this.rankingsService.getWeightedScoreByPosition(parameters).subscribe(
                (data) => {
                    this.fullDataset = data;
                    this.currentPeriodData = this._reusableFunctions.getFriendlyTenure(
                        data.filter((x) => x.isCurrentPeriod === true)
                    );
                    this.tableDataProperties.gridData = this.currentPeriodData;
                    this.tableDataProperties.hasData = true;
                    this.tableDataProperties.isDataLoaded = true;
                    this.tableDataProperties = Object.assign({}, this.tableDataProperties);
                },
                (error) => {
                    this.tableDataProperties.gridData = [];
                    this.tableDataProperties.hasData = false;
                    this.tableDataProperties.isDataLoaded = true;
                    this.tableDataProperties.action = NoDataActionType.error;
                    this.tableDataProperties = Object.assign({}, this.tableDataProperties);
                }
            );
        }
    }

    initiateTableProperties() {
        this.tableDataProperties = {
            action: this.action,
            gridData: [],
            searchTerm: '',
            componentTitle: 'Rank by Weighted Score',
            initialPageSize: 10,
            initialSkip: 0,
            height: 700,
            recordSelection: [],
            kendoGridSelectById: '',
            hasData: false,
            isDataLoaded: false,
            pageable: true,
            useLGScroll: false,
        };

        let columnProperties: ITableColumns[] = [
            {
                fieldId: 'rank',
                fieldName: 'Rank',
                isHyperlink: false,
                fieldOrder: 0,
                isSortable: true,
                defaultSorting: 'asc',
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                width: 50,
                filterable: false,
                filter: 'numeric',
            },
            {
                fieldId: 'employeeName',
                fieldName: 'Name',
                backUpFieldId: 'hierarchyField',
                isHyperlink: false,
                fieldOrder: 1,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 100,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'friendlyTenure',
                fieldName: 'Tenure',
                fieldOrder: 2,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                decimalPlaces: 0,
                width: 90,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'hierarchyName',
                fieldName: 'Branch',
                backUpFieldId: 'hierarchyField',
                isHyperlink: false,
                fieldOrder: 3,
                isSortable: true,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'string',
                width: 100,
                filterable: false,
                filter: 'text',
            },
            {
                fieldId: 'scoreValuesSparkline',
                fieldName: 'Score Trend',
                fieldOrder: 4,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 100,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'wgtScore',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Score',
                        datatype: null,
                        precision: 3,
                    },
                },
            },
            {
                fieldId: 'avgScore',
                fieldName: 'Avg Score',
                fieldOrder: 5,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                decimalPlaces: 2,
                width: 60,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'volumeValuesSparkline',
                fieldName: 'Volume Trend',
                fieldOrder: 6,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 100,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'volume',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Volume',
                        datatype: 'currency',
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'totalVolume',
                fieldName: 'Total Volume',
                fieldOrder: 7,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'currency',
                decimalPlaces: 0,
                width: 60,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
            {
                fieldId: 'unitValuesSparkline',
                fieldName: 'Units Trend',
                fieldOrder: 8,
                isSortable: false,
                headerAlignment: 'left',
                dataAlignment: 'left',
                dataType: 'chart',
                width: 100,
                filterable: false,
                hideIf: 'excel',
                chartProperties: {
                    chart: null,
                    dataType: 'number',
                    chartToFieldId: 'rank',
                    chartDataField: 'units',
                    inComponentChartBuilding: false,
                    tooltipProperties: {
                        labelField: 'Units',
                        datatype: null,
                        precision: 0,
                    },
                },
            },
            {
                fieldId: 'totalUnits',
                fieldName: 'Total Units',
                fieldOrder: 9,
                isSortable: true,
                headerAlignment: 'center',
                dataAlignment: 'center',
                dataType: 'number',
                decimalPlaces: 0,
                width: 60,
                filterable: false,
                filter: 'numeric',
                isDynamicColumn: true,
            },
        ];

        this.tableColumnProperties = {
            columnProperties: columnProperties,
        };
    }

    gridChangeEvent(event: IPaginatedDataInfo) {
        this.setColumnFormat();
        this.updateTableColumnProperties();
        this.tableColumnProperties = Object.assign({}, this.tableColumnProperties);
        this.paginatedData = event?.gridData;

        let chartParams: IChartParameters = {
            paginatedData: this.paginatedData,
            fullDataset: this.fullDataset,
            toGroupByColumnHeader: 'employeeName',
            calculateChange: true,
            columnList: this.tableColumnProperties.columnProperties,
            chartIdColumn: 'rank',
            chartType: 'area',
            color: 'rgb(124, 181, 236)',
        };

        setTimeout(() => {
            this.spco.setCharts(chartParams);
        }, 0);
    }

    updateTableColumnProperties() {
        let hierarchyFieldInf = this.tableColumnProperties.columnProperties.find(
            (colInf) => colInf.backUpFieldId === 'hierarchyField'
        );
    }

    setColumnFormat() {
        let columnsTobeFormatted = ['wgtScore'];
        let columnProperties = this.tableColumnProperties.columnProperties;

        columnsTobeFormatted.forEach((fieldId) => {
            let valueColumn = columnProperties.find((col) => col.fieldId === fieldId);
            if (valueColumn) {
                let format = this.currentPeriodData[0]?.format ?? valueColumn.dataType;
                let precision = this.currentPeriodData[0]?.precision ?? valueColumn.decimalPlaces;
                valueColumn.dataType = format as any;
                valueColumn.decimalPlaces = precision;
            }
        });
    }

    loadData() {
        let selectedPositionInfo = this.selectedPosition ? this.selectedPosition[0] : null;
        if (selectedPositionInfo) {
            this.pageHeadline.pageTitle = `${selectedPositionInfo.position} Rankings by Weighted Score`;
        }
        this.updateGrid();
    }

    updateGrid() {
        let rankData = this.searchResults();
        let orderedData = orderBy(rankData, this.rankEntrySort);
        this.exportData = JSON.parse(JSON.stringify(orderedData));
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
        this.updateTable(rankData);
        this.drawSparklineCharts(this.gridView.data);
    }

    updateTable(rankData) {
        this.tableDataProperties.gridData = rankData;
        this.tableDataProperties.hasData = rankData.length;
        this.tableDataProperties.isDataLoaded = true;
        this.tableDataProperties = Object.assign({}, this.tableDataProperties);
    }

    drawSparklineCharts(gridData: IRankings[]) {
        gridData.forEach((record) => {
            record.scoreChanges = this.calculateDateValueChanges(record.scoreValues);
            record.volumeChanges = this.calculateDateValueChanges(record.volumeValues);
            record.unitChanges = this.calculateDateValueChanges(record.unitValues);
            setTimeout(() => {
                this.drawCharts(record.scoreValues, record.scoreChanges, 'score', record.rank);
                this.drawCharts(record.volumeValues, record.volumeChanges, 'volume', record.rank);
                this.drawCharts(record.unitValues, record.unitChanges, 'units', record.rank);
            }, 0);
        });
    }

    pageChange(event: PageChangeEvent): void {
        this.pageSize = event.take;
        this.skip = event.skip;
        this.updateGrid();
    }

    sortChange(sort: SortDescriptor[]): void {
        this.rankEntrySort = sort;
        this.updateGrid();
    }

    public onStateChange(state: State) {
        this.gridState = state;
        this.loadData();
    }

    searchResults() {
        let searchedData: IWeightedScoreData[] = [];
        if (this.rankSearchTerm && this.rankSearchTerm.length > 0) {
            let searchTermTokens = this.rankSearchTerm.split(' ');
            searchedData = filterBy(this.currentPeriodData, {
                logic: 'or',
                filters: [
                    {
                        field: 'employeeName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
            if (searchTermTokens.length > 1) {
                searchedData = filterBy(searchedData, {
                    logic: 'or',
                    filters: [
                        {
                            field: 'employeeName',
                            operator: 'contains',
                            value: searchTermTokens[1],
                            ignoreCase: true,
                        },
                    ],
                });
            }
        } else {
            searchedData = this.currentPeriodData;
        }

        if (searchedData.length === 0) {
            this.noDataForCompany = true;
        }
        return searchedData;
    }

    searchRows(event) {
        this.tableDataProperties.isDataLoaded = false;
        this.skip = 0;
        this.rankSearchTerm = event;
        this.setNodataType(NoDataActionType.search);
        this.noDataType.searchTerm = this.rankSearchTerm;
        setTimeout(() => {
            this.updateGrid();
        }, 1000);
    }

    setNodataType(action: NoDataActionType) {
        if (this.rankSearchTerm && this.rankSearchTerm.length > 0) {
            this.noDataType.action = NoDataActionType.search;
        } else {
            this.noDataType.action = action;
        }
    }

    onSelectionChange(event) {
        this.skip = 0;
        this.setNodataType(NoDataActionType.filter);
        this.gridView = {
            data: [],
            total: 0,
        };
        this.getData();
    }

    onDeSelectionChange(event) {
        this.skip = 0;
        this.selectedPosition = [
            this.positionList.find((position) => position.position == event.position),
        ];
        this.setNodataType(NoDataActionType.filter);
        this.gridView = {
            data: [],
            total: 0,
        };
        this.getData();
    }

    /* Sparklines */

    /* Produce an array of changes between the values given by an array of units, volumes, or scores.
     *  Using two indices, the main index and a pivot index, we proceed to calculate the changes between each
     *  non-null value in the given array. The pivot marks the most recent element that is non-null (although the element COULD be null in the beginning),
     *  and the difference is calculated from this pivot and the next non-null found. The next non-null then becomes
     *  the new pivot, replacing the old one. Each non-change is marked as null, and all changes and non-changes
     *  are added to the final return array.
     */
    calculateDateValueChanges(values: number[]): number[] {
        // Precondition: Array must have more than one value
        if (!values || values.length <= 1) {
            return [];
        }

        let change: number = null;
        let changes: number[] = []; // Array of differences between two date periods
        let pivot: number = 0;
        for (let index = 1; index < values.length; index++) {
            if (values[pivot] == null || values[index] == null) {
                change = null;
                if (values[index] != null) {
                    pivot = index; // Make next non-null element the new pivot
                }
            } else {
                change = values[index] - values[pivot];
                pivot = index; // Make next non-null element the new pivot
            }
            changes.push(change);
        }
        return changes;
    }

    // Create charts objects with KPI Sparkline data
    drawCharts(values: Array<number>, changes: Array<number>, type: string, rank: number) {
        let dataType = type === 'volume' ? 'currency' : type;
        let divIdTrend = type + 'Trend' + rank;
        if (document.getElementById(divIdTrend)) {
            new Highcharts.Chart(
                divIdTrend,
                this.limeGearCharts.createKPISparkline(values, '', '', 'area', dataType)
            );
        }
        let divIdChanges = type + 'Changes' + rank;
        if (document.getElementById(divIdChanges)) {
            new Highcharts.Chart(
                divIdChanges,
                this.limeGearCharts.createKPISparkline(changes, '', '', 'column', dataType)
            );
        }
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName =
            `${this.selectedPosition[0].position}-performance-rankings` + dateTime + '_.xlsx';

        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        this.exportData.forEach((e) => {
            e.formattedVolume = this._reusableFunctions.formatMoney(e.volume);
        });

        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }
}
